import { Component, OnInit, Input, OnChanges } from "@angular/core";
import { AppText, ActionViewItemType, ToastPosition, mapChange } from "../interfaces/common";
import { DisplayService } from "../services/display.service";
import { LayerService } from "../services/layer.service";
import { MainActionViewService } from "../services/main-action-view.service";
import { Subscription } from "rxjs";

interface FooterItem {
  title: string;
  url?: string;
  imageUrl?: string; // Optional for images
  copy?: boolean;
  html?: string; // Optional HTML content to open in action view
  layerName?: string; // Optional: name of a mapLayer to toggle on/off
}

@Component({
  selector: "app-footer",
  templateUrl: "./footer.component.html",
  styleUrls: ["./footer.component.css"],
})
export class FooterComponent implements OnInit, OnChanges {
  @Input() selText: AppText = {};
  footerUrls: FooterItem[] = [];

  activeLayerNames: string[] = [];  // Array to keep track of active layer names
  private layerSubscription!: Subscription;

  constructor(private displayService: DisplayService, 
              private layerService: LayerService,
              private mainActionViewService: MainActionViewService) { }

  ngOnInit(): void {
    this.footerUrls = this.parseContents();

    // Listen to the stream. This catches actions from BOTH the footer and the timeline
    this.layerSubscription = this.layerService.getNewMaplayersStatus().subscribe((change: mapChange) => {
      const changedLayerNames = change.layers.map(layer => layer.name); // Map the array of mapLayer objects down to just an array of string names

      if (change.newCheckStatus) {
        // If true, layer was turned ON => add names to our active tracking array
        changedLayerNames.forEach(name => {
          if (!this.activeLayerNames.includes(name)) this.activeLayerNames.push(name);
        });
      }
      else {
        // If false, layer was turned OFF => remove names from our active tracking array
        this.activeLayerNames = this.activeLayerNames.filter(
          name => !changedLayerNames.includes(name)
        );
      }
    });
  }

  // A helper method that the HTML template can call to see if a button should be highlighted
  isLayerActive(layerName: string | undefined): boolean {
    if (!layerName) return false;
    return this.activeLayerNames.includes(layerName);
  }

  //Parse JSON object from selText
  parseContents(): FooterItem[] {
    if (this.selText.footerContent && this.selText.footerContent[0] == "[") {
      try {
        return JSON.parse(this.selText.footerContent) as FooterItem[];
      } catch (err) {
        console.error("Error parsing footer content:", err);
      }
    }
    return []
  }

  ngOnChanges(): void {
    this.footerUrls = this.parseContents()
  }

  ngOnDestroy(): void {
    if (this.layerSubscription) this.layerSubscription.unsubscribe();
  }

  openUrl(url: string | undefined): void {
    if (!url) return;
    /*
      noopener: Prevents the newly opened window from accessing the JavaScript window.opener property of the original page, which can help protect against certain types of phishing attacks.
      noreferrer: Prevents the browser from sending the referring page's URL to the new page.
    */
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  openHtmlInActionView(item: FooterItem): void {
    // Store the AppText key so the action view resolves the HTML dynamically on language change
    //this.displayService.mainActionViewItem$.next({
    this.mainActionViewService.sendNewMainActionViewItem({
      type: ActionViewItemType.information,
      text: item.title,
      closeable: true,
      html: item.html
    });
  }

  toggleLayer(layerName: string): void {
    const layer = this.layerService.mapLayers.find(ml => ml.name === layerName);
    if (!layer) return;
    this.layerService.setLayerChecked({ layers: [layer], newCheckStatus: !layer.checked, isSecondaryMap: false });
  }

  async copyToClipboard(text: string | undefined): Promise<void> {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text);
      this.displayService.toastrShowSuccess({
        message: this.selText.urlCopied,
        positionClass: ToastPosition.BottomCenter,
        tapToDismiss: true
      });
    } catch (err) {
      this.displayService.toastrShowError({
        message: this.selText.urlCopiedError,
        positionClass: ToastPosition.BottomCenter,
        tapToDismiss: true
      });
    }
  }
}